import PropTypes from 'prop-types'
import styled from 'styled-components'
import { useSelector } from 'react-redux'

import startCase from 'lodash/startCase'

import { uniqueId } from 'utils/strings'
import colors from 'components/utils/colors'
import { getFieldValue } from 'utils/ticketFields'

import * as sessionUserSelector from 'selectors/sessionUser'

import Table from 'components/Shared/TableNew/Index'
import TableHead from 'components/Shared/TableNew/TableHead'
import TableBody from 'components/Shared/TableNew/TableBody'
import TableFormRow from 'components/Shared/TableForm/TableFormRow'
import TableCell from 'components/Shared/TableNew/TableCell'
import ShowOnRowHover from 'components/Shared/TableNew/ShowOnRowHover'
import CopyIcon from 'components/Shared/CopyIcon/Index'
import ScrollArea from 'components/Shared/ScrollArea/Index'
import IconLink from 'components/Shared/IconLink/Index'
import Tooltip from 'components/Shared/Tooltip/Index'

const StyledScrollArea = styled(ScrollArea)`
  max-width: 1112px;
  max-height: 400px;
  margin-top: 16px;
  margin-left: -16px;
  overflow-y: auto;
`

const StyledTableFieldCopyIcon = styled(CopyIcon)`
  position: absolute;
  right: 16px;
  top: 16px;
`

const StyledTableFormLabelCell = styled(TableCell)`
  font-size: 14px;
  font-family: Exo;
  font-weight: 500;
  line-height: 16px;
  text-transform: uppercase;
  color: ${colors.secondary20};
  vertical-align: top;
  padding: 14px 0;
`

const CellContent = styled.div`
  padding: 0 0 0 16px;

  div[role="tooltip"] {
    font-family: 'Open Sans';
    text-transform: none;
  }
`

const TableFormTableFieldValueCellHeader = styled(TableCell)`
  padding: 4px 16px !important;
  font-size: 10px;
  color: ${colors.secondary60};
  font-family: Exo;
  font-weight: 600;
  background-color: #fff;
`

const TableFormTableFieldValueCell = styled(TableCell)`
  min-width: 182px;
  max-width:342px;
  padding: 12px 16px !important;
  font-size: 14px;
  color: ${colors.secondary40};
  font-family: 'Open Sans';
  font-weight: normal;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: normal;
  word-break: break-all;
`


const StyledHelpIcon = styled(IconLink)`
  margin-left: 4px;
  position: relative;
  top: 2px;
`

const renderHelpIcon = (tooltip) => {
  const tooltipId = uniqueId()
  if (!tooltip) {
    return null
  }

  return (
    <>
      <Tooltip id={tooltipId} />
      <StyledHelpIcon
        data-tooltip-content={tooltip}
        data-tooltip-id={tooltipId}
        iconName='icon-help-16'
      />
    </>
  )
}

const TableFormTableField = ({ field }) => {
  const userTimeZone = useSelector(sessionUserSelector.timezone)

  const generateTableId = (fieldName) => {
    return `ticket-table-fields-${fieldName}`
  }

  const renderTableValues = (fieldName, values) => {
    return (
      <Table width='100%' id={generateTableId(fieldName)}>
        <TableHead withTopBorder>
          <TableFormRow height={24}>
            {Object.keys(values[0]).map((key) => (
              <TableFormTableFieldValueCellHeader key={key}>
                {startCase(key)}
              </TableFormTableFieldValueCellHeader>
            ))}
          </TableFormRow>
        </TableHead>

        <TableBody hoverable={false}>
          {values.map((item, index) => (
            <TableFormRow height={40} key={index}>
              {Object.values(item).map((val, index) => {
                const fieldText = getFieldValue(val, userTimeZone)
                return (
                  <TableFormTableFieldValueCell key={index}>
                    {fieldText}
                  </TableFormTableFieldValueCell>
                )
              })}
            </TableFormRow>
          ))}
        </TableBody>
      </Table>
    )
  }

  return (
    <>
      <StyledTableFormLabelCell colSpan='3' withoutEllipsisTooltip>
        <CellContent>
          {startCase(field.name)}
          {field.tooltiptext && renderHelpIcon(field.tooltiptext)}

          <ShowOnRowHover>
            <StyledTableFieldCopyIcon tableId={generateTableId(field.name)} tooltipPlace='top' />
          </ShowOnRowHover>

          <StyledScrollArea>
            {renderTableValues(field.name, field.value)}
          </StyledScrollArea>
        </CellContent>
      </StyledTableFormLabelCell>
    </>
  )
}

TableFormTableField.propTypes = {
  field: PropTypes.object.isRequired,
}

export default TableFormTableField
